/**
 * cards/variants.js - Card Variant Definitions
 * Location: /app/frontend/src/admin/page_builder/cards/
 * 
 * Defines preset card templates for consistent presentation.
 * Cards are selected from presets, not custom-built.
 * 
 * Card Variants:
 * - reading: For reader content (chapters, pages)
 * - discovery: For browsing/exploration
 * - featured: Highlighted content with larger display
 * - creator: Creator profile cards
 */

/**
 * Card variant type constants
 */
export const CardVariant = {
  READING: 'reading',
  DISCOVERY: 'discovery',
  FEATURED: 'featured',
  CREATOR: 'creator',
};

/**
 * Human-readable labels for card variants
 */
export const CardVariantLabel = {
  [CardVariant.READING]: 'Reading Card',
  [CardVariant.DISCOVERY]: 'Discovery Card',
  [CardVariant.FEATURED]: 'Featured Card',
  [CardVariant.CREATOR]: 'Creator Card',
};

/**
 * Card variant descriptions
 */
export const CardVariantDescription = {
  [CardVariant.READING]: 'Compact card for chapter lists and reading progress',
  [CardVariant.DISCOVERY]: 'Standard card for browsing and exploration feeds',
  [CardVariant.FEATURED]: 'Large card for highlighted and promoted content',
  [CardVariant.CREATOR]: 'Profile card for creators with avatar and stats',
};

/**
 * Card variant style definitions
 */
export const CardVariantStyles = {
  [CardVariant.READING]: {
    // Compact horizontal layout for reading lists
    layout: 'horizontal',
    aspectRatio: 'auto',
    imageSize: 'small', // 60x80 thumbnail
    showTitle: true,
    showSubtitle: true, // Chapter number / page count
    showProgress: true, // Reading progress bar
    showMeta: false,
    showDescription: false,
    showActions: true, // Continue reading button
    padding: 'compact',
    borderRadius: 'md',
    hover: 'subtle',
  },
  [CardVariant.DISCOVERY]: {
    // Vertical card for browse grids
    layout: 'vertical',
    aspectRatio: '2/3', // Portrait cover
    imageSize: 'full',
    showTitle: true,
    showSubtitle: true, // Author/creator name
    showProgress: false,
    showMeta: true, // Genre, rating, etc.
    showDescription: false,
    showActions: false,
    padding: 'normal',
    borderRadius: 'lg',
    hover: 'lift', // Lifts on hover
  },
  [CardVariant.FEATURED]: {
    // Large card for featured content
    layout: 'vertical',
    aspectRatio: '16/9', // Wide hero image
    imageSize: 'large',
    showTitle: true,
    showSubtitle: true,
    showProgress: false,
    showMeta: true,
    showDescription: true, // Shows blurb
    showActions: true, // Read now, add to list
    padding: 'spacious',
    borderRadius: 'xl',
    hover: 'glow', // Orange glow on hover
  },
  [CardVariant.CREATOR]: {
    // Creator profile card
    layout: 'vertical',
    aspectRatio: '1/1', // Square avatar
    imageSize: 'avatar',
    showTitle: true, // Creator name
    showSubtitle: true, // @handle or role
    showProgress: false,
    showMeta: true, // Stats: followers, works
    showDescription: true, // Bio snippet
    showActions: true, // Follow button
    padding: 'normal',
    borderRadius: 'lg',
    hover: 'highlight',
  },
};

/**
 * CSS class mappings for card styles
 */
export const CardStyleClasses = {
  layout: {
    horizontal: 'flex flex-row items-center gap-3',
    vertical: 'flex flex-col',
  },
  aspectRatio: {
    'auto': '',
    '1/1': 'aspect-square',
    '2/3': 'aspect-[2/3]',
    '3/4': 'aspect-[3/4]',
    '16/9': 'aspect-video',
  },
  imageSize: {
    small: 'w-16 h-20 flex-shrink-0',
    avatar: 'w-24 h-24 rounded-full mx-auto',
    full: 'w-full',
    large: 'w-full h-48',
  },
  padding: {
    compact: 'p-2',
    normal: 'p-4',
    spacious: 'p-6',
  },
  borderRadius: {
    sm: 'rounded-sm',
    md: 'rounded-md',
    lg: 'rounded-lg',
    xl: 'rounded-xl',
  },
  hover: {
    none: '',
    subtle: 'hover:bg-zinc-800/50 transition-colors',
    lift: 'hover:-translate-y-1 hover:shadow-lg transition-all',
    glow: 'hover:ring-2 hover:ring-orange-500/50 hover:shadow-orange-500/20 hover:shadow-lg transition-all',
    highlight: 'hover:bg-zinc-800 transition-colors',
  },
};

/**
 * Get CSS classes for a card variant
 * @param {string} variant - Card variant key
 * @returns {Object} Object with class strings for each part
 */
export function getCardClasses(variant) {
  const styles = CardVariantStyles[variant] || CardVariantStyles[CardVariant.DISCOVERY];
  
  return {
    container: [
      CardStyleClasses.layout[styles.layout],
      CardStyleClasses.padding[styles.padding],
      CardStyleClasses.borderRadius[styles.borderRadius],
      CardStyleClasses.hover[styles.hover],
      'bg-zinc-900 border border-zinc-800 overflow-hidden',
    ].filter(Boolean).join(' '),
    image: [
      CardStyleClasses.imageSize[styles.imageSize],
      CardStyleClasses.aspectRatio[styles.aspectRatio],
      'object-cover',
      styles.imageSize === 'avatar' ? 'rounded-full' : CardStyleClasses.borderRadius[styles.borderRadius],
    ].filter(Boolean).join(' '),
    content: styles.layout === 'horizontal' ? 'flex-1 min-w-0' : 'p-3',
    title: 'font-semibold text-white truncate',
    subtitle: 'text-sm text-zinc-400 truncate',
    description: 'text-sm text-zinc-500 line-clamp-2 mt-1',
    meta: 'flex items-center gap-2 text-xs text-zinc-500 mt-2',
    actions: 'flex items-center gap-2 mt-3',
  };
}

/**
 * Get card variant definition
 * @param {string} variant - Card variant key
 * @returns {Object} Variant definition with all settings
 */
export function getCardVariant(variant) {
  return {
    variant,
    label: CardVariantLabel[variant] || 'Unknown',
    description: CardVariantDescription[variant] || '',
    styles: CardVariantStyles[variant] || CardVariantStyles[CardVariant.DISCOVERY],
    classes: getCardClasses(variant),
  };
}

/**
 * Get all card variants as array for UI selectors
 * @returns {Array<{value: string, label: string, description: string}>}
 */
export function getCardVariantOptions() {
  return Object.values(CardVariant).map(variant => ({
    value: variant,
    label: CardVariantLabel[variant],
    description: CardVariantDescription[variant],
  }));
}

/**
 * Default card variant
 */
export const DEFAULT_CARD_VARIANT = CardVariant.DISCOVERY;

export default {
  CardVariant,
  CardVariantLabel,
  CardVariantDescription,
  CardVariantStyles,
  CardStyleClasses,
  getCardClasses,
  getCardVariant,
  getCardVariantOptions,
  DEFAULT_CARD_VARIANT,
};
